import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Tabs, Table, Button, Select, DatePicker, message, Input } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { useSize, useDebounce } from 'ahooks';
import { useDispatch } from "react-redux";
import { setBreadcrumbList } from "../../store/menuInfo/menuInfo";
import apis from '../../apis';
import './index.scss';

const EquityRecord = () => {
    const dispatch = useDispatch();
    const [mode, setMode] = useState('enterprise'); // 'enterprise' | 'personal'
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const refTable = useRef(null);
    const refHeader = useRef(null);
    const size = useSize(refTable);
    const headerSize = useSize(refHeader);

    // Filters
    const [companyList, setCompanyList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [filterCompanyId, setFilterCompanyId] = useState(null);
    const [filterUserId, setFilterUserId] = useState(null);
    const [filterTimeRange, setFilterTimeRange] = useState(null);
    const [filterType, setFilterType] = useState(undefined);
    const [filterSummary, setFilterSummary] = useState('');
    const [companySearchLoading, setCompanySearchLoading] = useState(false);
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    const debouncedSummary = useDebounce(filterSummary, { wait: 500 });

    // Stats
    const [stats, setStats] = useState({
        total_recharge: 0,
        frozen: 0,
        deducted: 0,
        remaining: 0
    });

    const isEnterprise = mode === 'enterprise';

    // Search helpers
    const fetchCompanyList = (value) => {
        if (!value) {
            setCompanyList([]);
            return;
        }
        setCompanySearchLoading(true);
        // Use getCompanyList to match SubscriptionManagement
        apis.getCompanyList({ 
            company_name: value,
            version_type: 'ENTERPRISE,SUPER_GROUP,PRIVATE_DEPLOYMENT'
        }).then((res) => {
            setCompanySearchLoading(false);
            if (res.code === 200) {
                setCompanyList(res.data || []);
            }
        }).catch(() => {
            setCompanySearchLoading(false);
            setCompanyList([]);
        });
    };

    const fetchUserList = (value) => {
        if (!value) {
            setUserList([]);
            return;
        }
        setUserSearchLoading(true);
        apis.getUserListForTopup({
            user_name: value,
        }).then((res) => {
            setUserSearchLoading(false);
            if (res.code === 200) {
                setUserList(res.data || []);
            }
        }).catch(() => {
            setUserSearchLoading(false);
            setUserList([]);
        });
    };

    const debounceCompanyFetcher = useMemo(() => {
        return debounce(fetchCompanyList, 500);
    }, []);

    const debounceUserFetcher = useMemo(() => {
        return debounce(fetchUserList, 500);
    }, []);

    const handleSearchCompany = (value) => {
        debounceCompanyFetcher(value);
    };
    
    // Debounced user search
    const handleSearchUser = (value) => {
        debounceUserFetcher(value);
    };

    // Fetch Data
    const getDataFunc = () => {
        setLoading(true);
        const params = {
            record_type: isEnterprise ? 'ENTERPRISE' : 'PERSONAL',
            co_id: isEnterprise ? filterCompanyId : undefined,
            user_id: !isEnterprise ? filterUserId : undefined,
            equity_chg_type_code: filterType,
            summary: debouncedSummary,
            equity_chg_time_start: filterTimeRange?.[0] ? dayjs(filterTimeRange[0]).format("YYYY-MM-DD 00:00:00") : undefined,
            equity_chg_time_end: filterTimeRange?.[1] ? dayjs(filterTimeRange[1]).format("YYYY-MM-DD 23:59:59") : undefined,
            page: page,
            page_size: pageSize,
        };
        
        // Remove undefined params
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        apis.getEquityRecordList(params).then((res) => {
            if (res.code === 200) {
                setData(res.data?.data || []);
                setTotal(res.data?.total_count || 0);
                if (res.data?.stat) {
                    setStats({
                        total_recharge: res.data.stat.sum_topup_equity_point || 0,
                        frozen: res.data.stat.sum_freeze_equity_point || 0,
                        deducted: res.data.stat.sum_deduct_equity_point || 0,
                        remaining: res.data.stat.remaining_equity_point || 0
                    });
                }
            } else {
                setData([]);
                setTotal(0);
                message.error(res.message);
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            // message.error('获取权益记录失败');
        });
    };

    useEffect(() => {
        dispatch(setBreadcrumbList([{ title: '权益记录' }]));
    }, [mode]);

    useEffect(() => {
        getDataFunc();
    }, [page, pageSize, mode, filterCompanyId, filterUserId, filterType, filterTimeRange, debouncedSummary]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSummary]);

    // const handleSearch = () => {
    //     setPage(1);
    //     getDataFunc();
    // };

    // const handleReset = () => {
    //     setFilterCompanyId(undefined);
    //     setFilterUserId(undefined);
    //     setFilterTimeRange([]);
    //     setFilterType(undefined);
    //     setFilterSummary('');
    //     setPhoneNumber('');
    //     setPage(1);
    //     // Remove manual call to getDataFunc since useEffect will trigger it
    // };

    const handleDownload = () => {
        const params = {
            record_type: isEnterprise ? 'ENTERPRISE' : 'PERSONAL',
            co_id: isEnterprise ? filterCompanyId : undefined,
            user_id: !isEnterprise ? filterUserId : undefined,
            equity_chg_time_start: filterTimeRange?.[0] ? dayjs(filterTimeRange[0]).format("YYYY-MM-DD 00:00:00") : undefined,
            equity_chg_time_end: filterTimeRange?.[1] ? dayjs(filterTimeRange[1]).format("YYYY-MM-DD 23:59:59") : undefined,
        };
        
        apis.downloadEquityRecordList(params).then((res) => {
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            
            let fileName = `权益记录_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`;
            const contentDisposition = res.headers["content-disposition"];
            if (contentDisposition) {
                 const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                 const matches = filenameRegex.exec(contentDisposition);
                 if (matches != null && matches[1]) {
                     fileName = decodeURIComponent(matches[1].replace(/['"]/g, ''));
                 }
            }
            
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(link.href);
        }).catch(() => {
            message.error('下载失败');
        });
    };

    const columns = [
        ...(isEnterprise ? [{
            title: "企业(编号/名称)",
            key: "company",
            align: "center",
            // width: 200, // flexible
            render: (_, record) => `${record.co_code || '-'} / ${record.co_name || '-'}`
        }] : [{
            title: "用户(手机/昵称)",
            key: "user",
            align: "center",
            // width: 200, // flexible
            render: (_, record) => `${record.user_phone || '-'} / ${record.show_name || record.user_nickname || '-'}`
        }]),
        {
            title: "权益变动类型",
            dataIndex: "equity_chg_type_name",
            key: "equity_chg_type_name",
            align: "center",
            width: 120,
        },
        {
            title: "变动时间",
            dataIndex: "equity_chg_time",
            key: "equity_chg_time",
            align: "center",
            width: 180,
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: "摘要",
            dataIndex: "summary",
            key: "summary",
            align: "center",
            // width: 150, // flexible
        },
        {
            title: "备注",
            dataIndex: "remark",
            key: "remark",
            align: "center",
            ellipsis: true,
            // flexible
        },
        {
            title: "权益点",
            dataIndex: "equity_point",
            key: "equity_point",
            align: "center",
            width: 120,
            render: (text, record) => {
                const code = record.equity_chg_type_code;
                if (code === 'EQUITY_TOPUP') {
                    return <span style={{ color: '#52c41a' }}>+{Math.abs(text)}</span>;
                }
                if (code === 'EQUITY_FREEZE' || code === 'EQUITY_DEDUCT') {
                    return <span style={{ color: '#ff4d4f' }}>-{Math.abs(text)}</span>;
                }
                return <span>{text}</span>;
            }
        },
    ];

    return (
        <div className="equityRecordContent" ref={refTable}>
            <div ref={refHeader}>
                <div className="filterItemsBox">
                    <Tabs
                        activeKey={mode}
                        onChange={(key) => {
                            setMode(key);
                            setPage(1);
                            setFilterCompanyId(null);
                            setFilterUserId(null);
                            setFilterTimeRange(null);
                            setFilterType(undefined);
                            setFilterSummary('');
                            setCompanyList([]);
                            setUserList([]);
                        }}
                        className="modeTabs"
                        items={[
                            { key: 'enterprise', label: '企业' },
                            { key: 'personal', label: '个人' }
                        ]}
                        style={{ width: '100%' }}
                    />

                    <div className="filterItemsContent">
                        {isEnterprise ? (
                            <div className="filterItem">
                                <span className="filterItemSpan">企业编码/名称</span>
                                <Select
                                    showSearch
                                    placeholder="请输入企业编码/名称查询"
                                    value={filterCompanyId}
                                    onChange={(val) => {
                                        setFilterCompanyId(val);
                                        setPage(1);
                                    }}
                                    onSearch={handleSearchCompany}
                                    filterOption={false}
                                    notFoundContent={null}
                                    loading={companySearchLoading}
                                    style={{ width: 180 }}
                                    allowClear
                                    onClear={() => {
                                        setCompanyList([]);
                                        setFilterCompanyId(null);
                                    }}
                                    options={companyList.map(c => ({ 
                                        value: c.co_id, 
                                        label: (
                                            <div className="PersonAndCompanySelectItem">
                                                <a className="nameBox" title={c.co_name}>
                                                    {c.co_name}
                                                </a>
                                                <span className="codeBox">{c.co_code}</span>
                                            </div>
                                        )
                                    }))}
                                />
                            </div>
                        ) : (
                            <>
                                <div className="filterItem">
                                    <span className="filterItemSpan">用户手机号</span>
                                    <Select
                                        showSearch
                                        placeholder="请输入手机号查询"
                                        value={filterUserId}
                                        onChange={(val) => {
                                            setFilterUserId(val);
                                            setPage(1);
                                        }}
                                        onSearch={handleSearchUser}
                                        filterOption={false}
                                        style={{ width: 180 }}
                                        allowClear
                                        loading={userSearchLoading}
                                        notFoundContent={null}
                                        onClear={() => {
                                            setUserList([]);
                                            setFilterUserId(null);
                                        }}
                                        options={userList.map(u => ({ 
                                            value: u.user_id, 
                                            label: (
                                                <div className="PersonAndCompanySelectItem2">
                                                    <span className="mobileBox" title={u.mobile_no}>
                                                        {u.mobile_no}
                                                    </span>
                                                    <span className="nameBox" title={u.show_name} style={{ textAlign: "right" }}>
                                                        {u.show_name}
                                                    </span>
                                                </div>
                                            )
                                        }))}
                                    />
                                </div>

                            </>
                        )}
                        
                        <div className="filterItem">
                            <span className="filterItemSpan">权益变动时间</span>
                            <DatePicker
                                value={filterTimeRange?.[0]}
                                onChange={(date) => {
                                    const newRange = [...(filterTimeRange || [])];
                                    newRange[0] = date;
                                    setFilterTimeRange(newRange);
                                    setPage(1);
                                }}
                                style={{ width: 120 }}
                                placeholder="开始日期"
                                disabledDate={(current) => {
                                    const end = filterTimeRange?.[1];
                                    return end && current && current > end.endOf('day');
                                }}
                            />
                            &nbsp;&nbsp;~&nbsp;&nbsp;
                            <DatePicker
                                value={filterTimeRange?.[1]}
                                onChange={(date) => {
                                    const newRange = [...(filterTimeRange || [])];
                                    newRange[1] = date;
                                    setFilterTimeRange(newRange);
                                    setPage(1);
                                }}
                                style={{ width: 120 }}
                                placeholder="结束日期"
                                disabledDate={(current) => {
                                    const start = filterTimeRange?.[0];
                                    return start && current && current < start.startOf('day');
                                }}
                            />
                        </div>
                        <div className="filterItem">
                            <span className="filterItemSpan">权益变动类型</span>
                            <Select
                                placeholder="全部"
                                style={{ width: 120 }}
                                allowClear
                                value={filterType}
                                onChange={(val) => {
                                    setFilterType(val);
                                    setPage(1);
                                }}
                                options={[
                                    { value: 'EQUITY_TOPUP', label: '权益充值' },
                                    { value: 'EQUITY_DEDUCT', label: '权益扣除' },
                                    { value: 'EQUITY_FREEZE', label: '权益冻结' }
                                ]}
                            />
                        </div>
                        <div className="filterItem">
                            <span className="filterItemSpan">摘要</span>
                            <Input
                                placeholder="请输入摘要"
                                value={filterSummary}
                                onChange={(e) => setFilterSummary(e.target.value)}
                                style={{ width: 150 }}
                            />
                        </div>
                        
                        <div style={{ marginLeft: "auto" }}>
                            <Button type="primary" className="btn" icon={<DownloadOutlined />} onClick={handleDownload}>
                                下载
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="equityStatsBox">
                    <div className="statItem">
                        <span className="statLabel">累计充值权益点:</span>
                        <span className="statValue" style={{ color: "#006AB2" }}>{stats.total_recharge}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">冻结权益点:</span>
                        <span className="statValue" style={{ color: "#FE545F" }}>{stats.frozen}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">已扣除权益点:</span>
                        <span className="statValue" style={{ color: "#FE545F" }}>{stats.deducted}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">剩余权益点:</span>
                        <span className="statValue" style={{ color: "#61D4AD" }}>{stats.remaining}</span>
                    </div>
                </div>
            </div>

            <div className="tableContent">
                <Table
                    className="myFormTable"
                    dataSource={data}
                    columns={columns}
                    rowKey="equity_rec_id"
                    loading={loading}
                    bordered={true}
                    scroll={{ y: size?.height ? size.height - (headerSize?.height || 0) - 180 + 'px' : undefined }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                        onChange: (p, s) => {
                            setPage(p);
                            setPageSize(s);
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default EquityRecord;
